if (!customElements.get('product-set')) {
  customElements.define(
    'product-set',
    class ProductSet extends HTMLElement {
      constructor() {
        super();

        this.addAllButton = this.querySelector('[data-add-all-related-products]');
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      }

      connectedCallback() {
        if (this.addAllButton) {
          this.addAllButton.addEventListener('click', this.handleAddAllToCart.bind(this));
        }

        this.initProductImageZoomLightBox();
      }

      async handleAddAllToCart() {
        const productForms = this.querySelectorAll('product-info-multi form');
        const formData = new URLSearchParams();

        // Collect variant IDs and quantities from all forms
        productForms.forEach((form) => {
          const variantIdInput = form.querySelector('[name="id"]');
          const quantityInput = form.querySelector('[name="quantity"]');
          const variantId = variantIdInput ? variantIdInput.value : null;
          const quantity = quantityInput ? quantityInput.value : 1;

          if (variantId) {
            formData.append('items[][id]', variantId);
            formData.append('items[][quantity]', quantity);
          }
        });
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.body);
        }

        // Show loading state on the button
        this.addAllButton.setAttribute('aria-disabled', true);
        this.addAllButton.classList.add('loading');

        try {
          const response = await fetch(`${routes.cart_add_url}.js`, {
            method: 'POST',
            body: formData.toString(),
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
              'X-Requested-With': 'XMLHttpRequest',
            },
          }).then((res) => res.json());

          if (response.status === 422) {
            console.warn('Some items could not be added to the cart:', response.description);
          } else {
            if (this.cart) {
              // Render cart contents after products are added
              this.cart.renderContents(response);

              // Publish cart update event
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-set',
                cartData: response,
              });
            } else {
              // Redirect to the cart page if no cart element is available
              window.location.href = '/cart';
            }
          }
        } catch (error) {
          console.error('Error adding products to cart:', error);
        } finally {
          if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          // Reset button state
          this.addAllButton.removeAttribute('aria-disabled');
          this.addAllButton.classList.remove('loading');
        }
      }

      initProductImageZoomLightBox() {
        const options = {
          gallery: '.swiper-wrapper.contains-media',
          children: '.product-thumbnail-desktop a.zoom-light-box.image',
          mouseMovePan: true,
          initialZoomLevel: 3,
          secondaryZoomLevel: 'fit',
          maxZoomLevel: 4,
          wheelToZoom: true,
          pswpModule: PhotoSwipe,
        };
        const lightbox = new PhotoSwipeLightbox(options);
        const container = this.querySelectorAll(
          '.swiper-wrapper.contains-media .product-thumbnail-desktop img[class*="image-magnify"]'
        );

        lightbox.on('uiRegister', function () {
          lightbox.pswp.ui.registerElement({
            name: 'bulletsIndicator',
            className: 'pswp__bullets-indicator',
            appendTo: 'wrapper',
            onInit: (el, pswp) => {
              const bullets = [];
              let prevIndex = -1;

              for (const value of container) {
                value.classList.add('rimage__image');
                const galleryViewerThumb = document.createElement('a');
                galleryViewerThumb.classList.add('gallery-viewer-thumb');

                galleryViewerThumb.innerHTML =
                  `<div class="rimage-outer-wrapper">
                        <div class="rimage-wrapper">` +
                  value.outerHTML +
                  ` </div>
                      </div>`;
                el.append(galleryViewerThumb);
              }

              const imageList = el.querySelectorAll('img[class*="image-magnify"]');
              for (var i = 0; i < pswp.getNumItems(); i++) {
                const image = imageList[i];
                if (image) {
                  image.addEventListener('click', (e) => {
                    e.preventDefault();
                    pswp.goTo(bullets.indexOf(e.target));
                  });
                  bullets.push(image);
                }
              }

              pswp.on('change', (a) => {
                if (prevIndex >= 0) {
                  bullets[prevIndex].classList.remove('svifaded');
                }
                bullets[pswp.currIndex].classList.add('svifaded');
                prevIndex = pswp.currIndex;
              });
            },
          });
        });

        lightbox.init();
      }
    }
  );
}

if (!customElements.get('product-info-multi')) {
  customElements.define(
    'product-info-multi',
    class ProductInfoMulti extends HTMLElement {
      connectedCallback() {
        this.variantSelectors = this.querySelector('variant-selects-scoped');
        this.productForm = this.querySelector('form');
        this.addToCartButton = this.querySelector('button[type="submit"]');
        this.priceElement = this.querySelector('.product-related-item__price');

        // Parse variant data from the <script> tag
        const variantDataScript = this.querySelector('.product-variants-data');
        this.variants = variantDataScript ? JSON.parse(variantDataScript.textContent) : [];

        // Check if we're using the single dropdown configuration
        this.isSingleVariantSelector = this.variantSelectors.dataset.isSingleVariantSelector === 'true';

        if (this.isSingleVariantSelector) {
          this.singleVariantDropdown = this.variantSelectors.querySelector('select');
          if (this.singleVariantDropdown) {
            this.singleVariantDropdown.addEventListener('change', this.handleSingleVariantChange.bind(this));
          }
        } else if (this.variantSelectors) {
          this.variantSelectors.addEventListener('change', this.handleVariantChange.bind(this));
        }

        // Initialize the state based on the currently selected variant
        this.updateStateFromSelectedVariant();
        this.updateAvailableOptions(true); // Pass `true` to skip disabling initially selected options
      }

      handleSingleVariantChange(event) {
        const selectedVariantId = event.target.value;
        console.log('Selected variant ID:', selectedVariantId);

        if (!selectedVariantId || selectedVariantId === window.variantStrings.selectAnOption) {
          this.disableAddToCart(window.variantStrings.selectSize);
          return;
        }

        this.updateVariantInput(selectedVariantId);

        // Fetch variant-specific details (e.g., price, availability)
        this.fetchVariantDetails(selectedVariantId);
      }

      handleVariantChange(event) {
        // Update the state based on the selected variant
        this.updateStateFromSelectedVariant();

        // Update the availability of options
        this.updateAvailableOptions();
      }

      updateStateFromSelectedVariant() {
        const selectedVariantId = this.isSingleVariantSelector
          ? this.singleVariantDropdown?.value
          : this.getSelectedVariantId();

        if (!selectedVariantId || selectedVariantId === window.variantStrings.selectAnOption) {
          this.disableAddToCart(window.variantStrings.selectSize);
          return;
        }

        this.updateVariantInput(selectedVariantId);

        // Fetch variant-specific details (e.g., price, availability)
        this.fetchVariantDetails(selectedVariantId);
      }

      updateAvailableOptions(skipInitialSelection = false) {
        const selectedOptionValues = this.variantSelectors.selectedOptionValues;

        // Determine available options based on the selected values
        const availableOptions = this.getAvailableOptions(selectedOptionValues);

        // Update the UI to reflect available options
        this.updateOptionUI(availableOptions, skipInitialSelection);
      }

      getAvailableOptions(selectedOptionValues) {
        const availableOptions = {};

        this.variants.forEach((variant) => {
          if (variant.available && selectedOptionValues.some((value) => variant.options.includes(value))) {
            variant.options.forEach((optionValue, index) => {
              if (!availableOptions[index]) {
                availableOptions[index] = new Set();
              }
              availableOptions[index].add(optionValue);
            });
          }
        });

        return availableOptions;
      }

      updateOptionUI(availableOptions, skipInitialSelection = false) {
        this.variantSelectors.querySelectorAll('select, input[type="radio"]').forEach((input) => {
          const optionIndex = Number(input.dataset.optionIndex) - 1;

          if (isNaN(optionIndex)) {
            return;
          }

          const optionValue = input.value;
          const isAvailable = availableOptions[optionIndex]?.has(optionValue);
          const isSelected = input.checked || input.selected;

          // Always allow the currently selected option
          if (isSelected || (skipInitialSelection && isSelected)) {
            input.classList.remove('disabled');
            // input.closest('label')?.classList.remove('unavailable');
          } else if (isAvailable) {
            input.classList.remove('disabled');
            // input.closest('label')?.classList.remove('unavailable');
          } else {
            input.classList.add('disabled');
            // input.closest('label')?.classList.add('unavailable');
          }
        });
      }

      getSelectedVariantId() {
        const selectedOptionIds = this.variantSelectors.selectedOptionIds;

        if (!selectedOptionIds || selectedOptionIds.length === 0) {
          return null;
        }

        const selectedOptionNames = selectedOptionIds.map((id) => {
          const optionElement = this.variantSelectors.querySelector(`[data-option-value-id="${id}"]`);
          return optionElement ? optionElement.value.trim() : null;
        });

        if (selectedOptionNames.includes(null)) {
          return null;
        }

        const matchingVariant = this.variants.find((variant) =>
          selectedOptionNames.every((value, index) => variant.options[index] === value)
        );

        return matchingVariant ? matchingVariant.id : null;
      }

      updateVariantInput(variantId) {
        const variantInput = this.productForm.querySelector('input[name="id"]');
        if (variantInput) {
          variantInput.value = variantId;
        }
      }

      fetchVariantDetails(variantId) {
        console.log('this.variants:', this.variants);
        const variant = this.variants.find((v) => Number(v.id) === Number(variantId));
        if (!variant) {
          console.error('Variant not found in variant data.');
          this.disableAddToCart(window.variantStrings.unavailable);
          return;
        }

        // Update price
        if (this.priceElement) {
          this.priceElement.textContent = `$${(variant.price / 100).toFixed(2)}`;
        }

        // Enable Add to Cart button if the variant is available
        if (variant.available) {
          this.enableAddToCart();
        } else {
          this.disableAddToCart(window.variantStrings.unavailable);
        }
      }

      enableAddToCart() {
        if (this.addToCartButton) {
          this.addToCartButton.disabled = false;
          this.addToCartButton.textContent = window.variantStrings.addToCart;
        }
      }

      disableAddToCart(message = window.variantStrings.unavailable) {
        if (this.addToCartButton) {
          this.addToCartButton.disabled = true;
          this.addToCartButton.textContent = message;
        }
      }
    }
  );
}
